// hooks/useVideoPlayer.ts
// Custom hook for video player management

import { useRef, useEffect, useCallback } from 'react'
import { videos, isPlanVariantAction, PlanVariantAction } from '../videoConfig'
import { AVATARS, ACCOUNT_TYPES, ACTION_TYPES } from '../constants'

interface UseVideoPlayerProps {
  currentIndex: number
  selectedAvatar: string | null
  selectedLanguage: string | null
  selectedVariantIntro: string | null
  onVideoChange?: (videoTitle: string, videoSrc: string, index: number) => void
}

export function useVideoPlayer({
  currentIndex,
  selectedAvatar,
  selectedLanguage,
  selectedVariantIntro,
  onVideoChange
}: UseVideoPlayerProps) {
  const videoRef = useRef<HTMLVideoElement | null>(null)
  const sourceRef = useRef<HTMLSourceElement | null>(null)

  // Determine video source based on avatar, account type, and language
  const getVideoSource = useCallback(() => {
    const videoObj = videos[currentIndex]
    if (!videoObj) return null

    let srcToLoad: string | undefined

    // Determine language suffix (default to CZ if language not selected)
    const lang = selectedLanguage === 'sk' ? 'sk' : 'cz'

    // Check if this is an account-specific video
    const isAccountVideo = isPlanVariantAction(videoObj.action)

    if (!isAccountVideo) {
      // Regular video - choose based on avatar and language
      if (selectedAvatar === AVATARS.ROBERT) {
        srcToLoad = (lang === 'cz' ? videoObj.src_r_cz : videoObj.src_r_sk) ??
                    (lang === 'cz' ? videoObj.src_cz : videoObj.src_sk) ??
                    undefined
      } else if (selectedAvatar === AVATARS.SILVIE) {
        srcToLoad = (lang === 'cz' ? videoObj.src_s_cz : videoObj.src_s_sk) ??
                    (lang === 'cz' ? videoObj.src_cz : videoObj.src_sk) ??
                    undefined
      } else {
        srcToLoad = (lang === 'cz' ? videoObj.src_cz : videoObj.src_sk) ??
                    (lang === 'cz' ? videoObj.src_r_cz : videoObj.src_r_sk) ??
                    (lang === 'cz' ? videoObj.src_s_cz : videoObj.src_s_sk) ??
                    undefined
      }
    } else {
      // Account-specific video - choose based on account type, avatar, AND language
      const action = videoObj.action as PlanVariantAction
      if (selectedVariantIntro === ACCOUNT_TYPES.GOLD) {
        srcToLoad = selectedAvatar === AVATARS.ROBERT
          ? (lang === 'cz' ? action.src_gold_r_cz : action.src_gold_r_sk) ?? undefined
          : (lang === 'cz' ? action.src_gold_s_cz : action.src_gold_s_sk) ?? undefined
      } else if (selectedVariantIntro === ACCOUNT_TYPES.RENTIER) {
        srcToLoad = selectedAvatar === AVATARS.ROBERT
          ? (lang === 'cz' ? action.src_rentier_r_cz : action.src_rentier_r_sk) ?? undefined
          : (lang === 'cz' ? action.src_rentier_s_cz : action.src_rentier_s_sk) ?? undefined
      } else if (selectedVariantIntro === ACCOUNT_TYPES.MAX) {
        srcToLoad = selectedAvatar === AVATARS.ROBERT
          ? (lang === 'cz' ? action.src_max_r_cz : action.src_max_r_sk) ?? undefined
          : (lang === 'cz' ? action.src_max_s_cz : action.src_max_s_sk) ?? undefined
      }
    }

    return srcToLoad
  }, [currentIndex, selectedAvatar, selectedVariantIntro, selectedLanguage])

  // Load video when index or selections change
  useEffect(() => {
    const v = videoRef.current
    const s = sourceRef.current
    if (!v) return

    const srcToLoad = getVideoSource()
    if (!srcToLoad) return

    // Hard stop current stream to prevent audio overlap (Safari/Edge fix)
    try { v.pause() } catch (e) {}
    v.removeAttribute('src')
    if (s) s.src = ''
    s.src = srcToLoad
    videoRef.current.loop = false

    v.currentTime = 0
    v.load()

    // Send analytics event
    if (onVideoChange) {
      const videoTitle = videos[currentIndex]?.title_cz ?? ''
      onVideoChange(videoTitle, srcToLoad, currentIndex)
    }

    // Enable loop for avatar selection video
    if (videoRef.current && videos[currentIndex].action?.type === ACTION_TYPES.SELECT_AVATAR) {
      videoRef.current.loop = true
    }
  }, [currentIndex, selectedAvatar, selectedLanguage, selectedVariantIntro, getVideoSource, onVideoChange])

  return {
    videoRef,
    sourceRef
  }
}
